import { Controller } from "@hotwired/stimulus"
import { PENDING_REFRESH_KEY, saveAllFormSelections } from "../pick_selection_storage"

const EMPTY_MESSAGE = "Select at least one contact."
const SELECT_ALL_LABEL = "Select all"
const DESELECT_ALL_LABEL = "Deselect all"
const GENERIC_ERROR_MESSAGE = "Something went wrong. Please try again."

export default class extends Controller {
  static targets = [ "error", "toggle" ]

  static values = {
    mobile: Boolean
  }

  connect() {
    this.updateToggleLabel()
  }

  async submit(event) {
    const checked = this.emailCheckboxes().filter((checkbox) => checkbox.checked)
    if (checked.length === 0) {
      event.preventDefault()
      this.showError(EMPTY_MESSAGE)
      return
    }

    this.hideError()
    sessionStorage.setItem(PENDING_REFRESH_KEY, "1")
    saveAllFormSelections()

    if (!this.mobileValue) return

    event.preventDefault()

    const submitButton = this.element.querySelector('button[type="submit"]')
    if (submitButton) submitButton.disabled = true

    try {
      const response = await fetch(this.element.action, {
        method: "POST",
        body: new FormData(this.element),
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": this.csrfToken()
        },
        credentials: "same-origin"
      })

      const data = await response.json()

      if (!response.ok) {
        this.showError(data.error || EMPTY_MESSAGE)
        return
      }

      this.openComposeUrl(data.compose_url)
    } catch (_error) {
      this.showError(GENERIC_ERROR_MESSAGE)
    } finally {
      if (submitButton) submitButton.disabled = false
    }
  }

  toggleSelection() {
    if (this.allChecked()) {
      this.emailCheckboxes().forEach((checkbox) => {
        checkbox.checked = false
      })
    } else {
      this.emailCheckboxes().forEach((checkbox) => {
        checkbox.checked = true
      })
      this.clearError()
    }

    this.updateToggleLabel()
    saveAllFormSelections()
  }

  onCheckboxChange() {
    this.clearError()
    this.updateToggleLabel()
    saveAllFormSelections()
  }

  openComposeUrl(url) {
    const link = document.createElement("a")
    link.href = url
    link.rel = "noopener noreferrer"
    link.style.display = "none"
    document.body.appendChild(link)
    link.click()
    link.remove()
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }

  emailCheckboxes() {
    return Array.from(this.element.querySelectorAll('input[name="emails[]"]'))
  }

  allChecked() {
    const checkboxes = this.emailCheckboxes()
    return checkboxes.length > 0 && checkboxes.every((checkbox) => checkbox.checked)
  }

  updateToggleLabel() {
    if (!this.hasToggleTarget) return

    const label = this.allChecked() ? DESELECT_ALL_LABEL : SELECT_ALL_LABEL
    this.toggleTarget.textContent = label
    this.toggleTarget.setAttribute("aria-label", label)
  }

  clearError() {
    this.hideError()
  }

  showError(message) {
    if (!this.hasErrorTarget) return

    this.errorTarget.textContent = message
    this.errorTarget.classList.remove("hidden")
    this.errorTarget.scrollIntoView({ block: "nearest", behavior: "smooth" })
  }

  hideError() {
    if (!this.hasErrorTarget) return

    this.errorTarget.textContent = ""
    this.errorTarget.classList.add("hidden")
  }
}
